<template>
  <tile class="opc-pbx opc-pbx-regional">
    <foldable v-model="isExpanded" :title="this.$t('billing.planform.title.zoomphone')">
      <template #head>
        <div class="opc-pbx-head">
          <div class="opc-pbx-head__logo--left">
            <img class="opc-pbx-regional__logo-icon" :src="ZoomPhoneIcon" :alt="$t('billing.planform.title.zoomphone')" />
          </div>
          <div class="opc-pbx-head__logo">
            <div class="opc-pbx-regional__logo opc-pbx__title-wrapper">
              Zoom Phone
            </div>
            <div class="opc-config__collapse" v-if="!isExpanded">
              <div v-for="selectedPlansLabel in selectedPlansLabelList" :key="selectedPlansLabel">
                {{ selectedPlansLabel }}
              </div>
              <div>{{ callingFeesLabel }}</div>
            </div>
          </div>
          <zm-button v-if="isExpanded" data-testid="checkout-button-209" type="text" @click="showPlanDetailDialog">
            {{ $t('billing.plan_details') }}
          </zm-button>
        </div>
      </template>
      <div class="opc-pbx__main">
        <!--What calling plans do your users need.-->
        <p class="label-title-1">
        <span id="label-title-1">{{ $t('billing.pbx.what_calling_plans') }}</span>
        <zm-tooltip
          placement="top"
          :content="$t('billing.pbx.calling_plan_selected_tips')"
          @show="showCallingPlanToolTip"
        >
          <tips-icon data-testid="checkout-button-210" :aria-label="$t('billing.pbx.what_calling_plans')" />
        </zm-tooltip>
      </p>
       <zm-radio-group aria-labelledby="label-title-1" class="opc-tag-radio" name="calling-plans-group" v-model="activeRegionKey" @change='onRadioChange'>
          <zm-radio data-testid="checkout-radio-211" v-for="region in regionsList" :key="region.key" :label="region.key" :class="showCheckClass(region)">
            <span v-html="getCallingPlanLabel(region)"></span>
          </zm-radio>
        </zm-radio-group>
        <zm-banner v-if="totalLimitedPlansCount > maxLimitedPlansCount" class="number-item" type="warning">
          <div ref="numberTipsRef" v-html="$t('billing.pbx.more_than_numbers', [maxLimitedPlansCount, pbxContactSalesLink])"></div>
        </zm-banner>
       <div :key="activeRegionKey+','+selectedPbxBillingCycle">
          <config-section :show-head="!!regionsConfig[activeRegionKey].callings.length" :show-action="!testRegionalCallingPlansFull(selectedPbxBillingCycle)">
            <template #title>
              <span>{{ $t('billing.pbx.calling_plans') }}</span>
            </template>
            <template #description>
              <span>{{ callingPlansDesc }}</span>
            </template>
            <template v-if="addAnotherCallingCtaVisible" #action>
              <zm-button data-testid="checkout-button-212" type="text" @click="addAnotherCallingPlan">
                {{ $t('billing.pbx.add_another_calling_plan') }}
              </zm-button>
            </template>
            <number-select
              ref="callingSelect"
              :key="`calling-${componentKey}`"
              :data-tracking="dataTrackingCallingPlan"
              :optionList="mapSelectOptions(regionalCallingPlans, selectedPbxBillingCycle)"
              :deletable="callingPlanDeletable"
              :before-change="beforeChangeCallingPlan"
              :title="$t('billing.pbx.calling_plans')"
              v-model="regionsConfig[activeRegionKey].callings"
            />
          </config-section>
          <config-section :show-head="!!regionsConfig[activeRegionKey].additions.length" :show-action="!testRegionalAdditionalPlansFull(selectedPbxBillingCycle)">
            <template #title>
              <span>{{ $t('billing.pbx.additional_numbers') }}</span>
            </template>
            <template #description>
              <span>
                {{ $t('billing.pbx.additional_numbers_desc') }}
              </span>
            </template>
            <template #action>
              <zm-tooltip
                placement="top"
                :disabled="regionsConfig[activeRegionKey].additions.length > 0"
                :content="$t('billing.pbx.add_additional_numbers_tips')"
                @show="showAdditionalNumbersToolTip"
              >
                <zm-button data-testid="checkout-button-213" type="text" @click="addAdditionalNumbers">
                  {{ $t('billing.pbx.add_additional_numbers') }}
                </zm-button>
              </zm-tooltip>
            </template>
            <number-select
              ref="tollfreeSelect"
              :key="`additional-${componentKey}`"
              :data-tracking="dataTrackingNumbers"
              :optionList="mapSelectOptions(regionalAdditionalPlans, selectedPbxBillingCycle)"
              :title="$t('billing.pbx.additional_numbers')"
              v-model="regionsConfig[activeRegionKey].additions"
            />
          </config-section>
        </div>
        <!-- The method of the component instance will be called through ref(pbxCardRef) to facilitate the reuse of code logic -->
        <opcCardCascade ref="pbxCardRef" v-model="billingCycleModel" divide-line :tracking-data='planAndCycleTrackingData' :config="cycleAndPlansConfig" :billing-cycle-switch-visible="billingCycleSwitchVisible" @change="switchPlansCycle">
          <template #group1-title>
            <div>
              <span id="group1-title">{{ callingFeeLabel }}</span>
              <zm-popover placement="top" trigger="hover" @show="showCallingfeesToolTip">
                <template #content>
                  <p v-html="$t('billing.pbx.calling_fee_tips', [activeRegionLabel])"></p>
                </template>
                <tips-icon data-testid="checkout-button-214" :aria-label="callingFeeLabel" />
              </zm-popover>
            </div>
          </template>
        </opcCardCascade>
        <appeal-byoc v-if="isVisibleAppealByoc" />
        <div class="opc-zm-base-card__main__footer" v-if="isShowRemoveBtn">
          <zm-button data-testid="checkout-button-119" type="text" @click="removeAllPlans">{{ $t('billing.common.remove') }}</zm-button>
        </div>
      </div>
    </foldable>
  </tile>
</template>

<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import OpcCardCascade from '../component/opc-card-cascade';
import AppealByoc from '../component/appeal-byoc.vue';
import ZoomPhoneIcon from '@/assets/plans/logo_pbx.svg';
import {
  computedPbxBasePlans,
  computedPbxCallingPlans,
  computedPbxPlans,
  PBX_SUB_TYPES
} from '@/common/service/computers/plans/pbx';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { BILLING_CYCLE, BUNDLE_WITH_ZP_BASE_PLAN_TYPES } from '@/common/constants';
import { groupBy } from 'lodash';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';
import plansMixin from '@/plans/mx/plan';
import ConfigSection from '../component/config-section.vue';
import NumberSelect from '../component/select-number-list.vue';
import mxEdit from './mx-edit';
import merge from 'deepmerge'
import { getQueryByLocationSearch } from '@/utils/index';
import {
  trackingAddAdditionalNumbers,
  trackingAddAnotherCallingPlan,
  trackingSelectCallingPlan,
  getZPCallingFeesText,
  canRemoveZoomPhone,
  getZPCallingFeesCollapseText
} from '@/plans/ZOOM_PHONE/helper/utils';
import {planTypes} from '@/common/constants';
import mxSoftBundleCustom from '../mixin/soft-bundle-pbx';
import { canRemoveAddOnPlanInWinBack } from '@/views/config/shopping-cart/persist/plan-remove';

export default {
  key: planTypes[planTypes.ZOOM_PHONE],
  data() {
    return {
      ZoomPhoneIcon,
      isExpanded: true,
      activeRegionKey: '',
      form: {
        category: '',
        localNumber: 0,
        tollFreeNumber: 0,
      },
    };
  },
  components: {
    Foldable,
    Tile,
    OpcCardCascade,
    ConfigSection,
    NumberSelect,
    AppealByoc,
  },
  created() {
    this.initPbxForm().then(() => this.numberListLoading = false);
  },
  mounted() {
    this.setPbxCardInstance();
  },
  mixins: [plansMixin, mxEdit, mxSoftBundleCustom],
  props: {
    region: {
      // show top-right drop down arrow
      type: String,
      default: 'JP',
    },
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters(['pbxBaseInfo']),
    pbxBillingCycle: {
      get() {
        return this.pbxBaseInfo.billingCycle;
      },
      set(newValue) {
        this.setPbxBillingCycle(newValue);
      }
    },
    pbxBasePlanCode: {
      get() {
        return this.pbxBaseInfo.basePlanCode;
      },
      set(newValue) {
        this.setPbxBasePlanCode(newValue);
      }
    },
    billingCycleModel: {
      get() {
        return [this.pbxBaseInfo.billingCycle, this.pbxBaseInfo.basePlanCode];
      },
      set(val) {
        const [billingCycle, usageFeePlanCode] = val
        this.setPbxBillingCycle(billingCycle);
        this.setPbxBasePlanCode(usageFeePlanCode);
      }
    },
    isShowRemoveBtn() {
      if(this.$context.is.fromPersistentCart)return true;
      if (canRemoveAddOnPlanInWinBack(planTypes.ZOOM_PHONE)) {
        return true;
      }
      return canRemoveZoomPhone();
    },
    basePlans() {
      return computedPbxBasePlans(this.planMapFlat);
    },
    cycleAndPlansConfig() {
      const { annual, monthly, payAsYouGo } = groupBy(this.basePlans, 'billing_cycle');
      return {
        group0: {
          title: this.$t('billing.select_billing_cycle'),
        },
        group1: {
          slot: 'group1-title',
        },
        options: ['monthly', 'annual'].map(cycle => {
          return {
              value: cycle,
              title: this.$t(`billing.billing_cycle.${cycle}`),
              subTitle: this.$money(this.costMap[cycle], { forceDiff: true }),
              description: cycle === 'monthly' ? this.$t('billing.monthly_cost') : this.$t('billing.annual_cost'),
              showSave: BILLING_CYCLE.ANNUAL === cycle,
              ...this.costMap,
              options: [...payAsYouGo, ...(cycle === 'monthly' ? monthly : annual)].map(plan => {
                return {
                  value: plan.plan_code,
                  title: this.getCallingFeesText(plan).title,
                  subTitle: this.$money(plan.amount, { forceDiff: true }),
                  description: this.getCallingFeesText(plan).unit,
                }
              })
            }
        })
      }
    },
    planAndCycleTrackingData() {
      return [{ buyInteraction: 'zoom phone subscription cycle', }, { buyInteraction: 'zoom phone international calling fees', },];
    },
    allPbxPlans() {
      return computedPbxPlans(this.planMapFlat);
    },
    selectedBasePlan() {
      return computedPbxBasePlans(this.getSelectedPlans)[0];
    },
    selectedCallingPlan() {
      return computedPbxCallingPlans(this.getSelectedPlans)[0];
    },
    selectedTFPlan() {
      return computedPbxPlans(this.getSelectedPlans).find(item => item.sub_type === PBX_SUB_TYPES.TOLLFREE);
    },
    defaultTFPlan() {
      return this.allPbxPlans.find(item => (
        item.region === PBX_REGIONS.JP &&
        item.billing_cycle === this.pbxBillingCycle &&
        item.sub_type === PBX_SUB_TYPES.TOLLFREE
      ));
    },
    costMap() {
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => (
        item.sub_type === PBX_SUB_TYPES.UNLIMITED ||
        item.sub_type === PBX_SUB_TYPES.METERED ||
        item.sub_type === PBX_SUB_TYPES.TOLLFREE ||
        item.sub_type === PBX_SUB_TYPES.PHONE_NUMBER
      ))
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      const monthlyPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.MONTHLY);
      const annualPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.ANNUAL);
      const _costMap = {
        [BILLING_CYCLE.MONTHLY]: monthlyPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
        [BILLING_CYCLE.ANNUAL]: annualPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
      };
      return _costMap;
    },
    tollFreeMax() {
      return 99 - (this.totalPhoneNumbers - this.form.tollFreeNumber);
    },
    numberTips() {
      if (this.totalPhoneNumbers >= 99) {
        return {
          text: this.$t('billing.pbx.more_than_99_license', [this.pbxContactSalesLink]),
          type: 'warning',
        };
      }
      return null;
    },
    callingFeeLabel() {
      if(!this.selectedCallingPlan) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.UNLIMITED) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.METERED) return this.$t('billing.pbx.calling_fee_label.metered');
      return this.$t('billing.pbx.calling_fee_label.unlimited');
    },
    callingFeeName() {
      if(!this.selectedBasePlan) return '';
      return this.$t('billing.pbx.metered_calling_credits');
    },
    selectedPbxBillingCycle() {
      const selectedCallingPlans = computedPbxCallingPlans(this.selectedPbxPlans);
      return selectedCallingPlans?.[0]?.billing_cycle; // HACK implicit dependency that calling plans are same on billing_cycle
    },
    callingPlansDesc() {
      return this.$t(this.regions.find(r => r.key === this.activeRegionKey).desc);
    },
    regionsList() {
      const list = this.regions.filter(region => this.inRegionList(region));
      return this.filterRegions(list, this.activeRegionKey);
    },
    callingFeesLabel() {
      return getZPCallingFeesCollapseText({
        billingCycle: this.selectedBasePlan.billing_cycle,
        amount: this.selectedBasePlan.amount,
        name: this.$t('billing.pbx.calling_fee_name.pay_you_go.UC'),
      })
    },
    // display name of currently selected region label
    activeRegionLabel() {
      return this.getRegionLabelById(this.activeRegionKey)
    },
    addAnotherCallingCtaVisible() {
      if (this.soft_bundle_hiddenTypeSwitch) return false;
      return this.enableJPBuy;
    },
    billingCycleSwitchVisible() {
      if (this.soft_bundle_hiddenCycleSelection) return false;
      return true;
    },
    isVisibleAppealByoc() {
      if (this.soft_bundle_hiddenAppealByoc) return false;
      return true;
    },
  },
  methods: {
    ...mapMutations({
      updateSelectPlan: 'updateSelectPlan',
      setPbxBasePlanCode: 'setPbxBasePlanCode',
      setPbxBillingCycle: 'setPbxBillingCycle',
      batchRemovePlan: 'batchRemovePlan'
    }),
    ...mapActions(['removePlanByKey', 'groomDraftPlanFilterKeys']),
    getCallingFeesText: getZPCallingFeesText,
    addAnotherCallingPlan() {
      this.$refs.callingSelect.newItem();
      trackingAddAnotherCallingPlan(this.getRegionLabelById(this.activeRegionKey));
    },
    addAdditionalNumbers() {
      this.$refs.tollfreeSelect.newItem();
      trackingAddAdditionalNumbers(this.getRegionLabelById(this.activeRegionKey));
    },
    onRadioChange(val) {
      trackingSelectCallingPlan(this.getRegionLabelById(val));
    },
    updateCallingPlan(planCode,number) {
      this.updateSelectPlan({
        plan_code: planCode,
        cartInfo: { selected_number: number  },
      });
    },

    async initPbxForm() {
      if(this.selectedBasePlan) {
        this.pbxBasePlanCode = this.selectedBasePlan.plan_code;
      }
      if(this.selectedCallingPlan) {
        this.pbxBillingCycle = this.selectedCallingPlan.billing_cycle;
        this.form.category = this.selectedCallingPlan.category;
      } else if(!this.selectedBasePlan.is_payg) {
        this.pbxBillingCycle = this.selectedBasePlan.billing_cycle;
      } else {
        this.pbxBillingCycle = BILLING_CYCLE.ANNUAL;
      }

      if(this.selectedTFPlan) {
        this.form.tollFreeNumber = Number(this.selectedTFPlan.cartInfo.selected_number);
      }

      this.activeRegionKey = getQueryByLocationSearch()?.region || this.region || PBX_REGIONS.JP
      this.fillSelectModel(this.regionsConfig);
    },

    handleBillingCycleChange(value) {
      this.selectedPbxPlans
      .filter(item => item.sub_type === PBX_SUB_TYPES.BASE)
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => item.sub_type !== PBX_SUB_TYPES.BASE)
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      this.switchPlansBillingCycle(selectedCategoryNumberMap, value);
    },
    switchPlansBillingCycle(categoryNumberMap, billingCycle) {
      this.allPbxPlans
        // get selected plans
        .filter(item => categoryNumberMap[item.category])
        // remove selected plans
        .forEach(item => this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: 0 } }))
        // get plans by billing cycle

      this.allPbxPlans
        .filter(item => categoryNumberMap[item.category] && item.billing_cycle === billingCycle)
        // add plan to cart
        .forEach(item => {
          if(categoryNumberMap[item.category]) {
            this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: categoryNumberMap[item.category] } })
            categoryNumberMap[item.category] = 0;
          }
        });
      const overwriteMerge = (destinationArray, sourceArray) => sourceArray
      this.regionsConfig = merge(this.regionsConfig,this.fillSelectModel(),{ arrayMerge: overwriteMerge })
    },
    switchPlansCycle(billingCycleInfo) {
      this.handleBillingCycleChange(billingCycleInfo[0])
      this.handleBasePlanChange(billingCycleInfo[1])
    },
    handleBasePlanChange(value) {
      this.updateSelectPlan({
        plan_code: this.selectedBasePlan.plan_code,
        cartInfo: { selected_number: 0 },
      });
      this.updateSelectPlan({
        plan_code: value,
        cartInfo: { selected_number: 1 },
      });
    },
    isBuyOrAddJP(){
      return this.$context.is.buynow || this.$context.is.add2cart
    },
    inRegionList(region){
      const regions = [
      ];

    // Remove UK/IR from the list depends on the toggle status
    if (!this.$config('hideZoomPhoneUKIRPlans') && !this.isBuyOrAddJP) regions.push(PBX_REGIONS.UK_AND_IRELAND);

    if (!this.$config('hideZoomPhoneJPPlans') && this.enableJPBuy) regions.push(PBX_REGIONS.JP);

    if(this.$context.is.add2cart) regions.push(PBX_REGIONS.US_AND_CANADA); // for add2cart, reuse this module to config 3 regions at one time
      return regions.includes(region.key);
    },
    updateCallingPlansToCart(plans){
      !!plans && plans.map(c => this.updateCallingPlan(c.id,c.number))
    },
    removeAllPlans () {
      this.removePlanByKey(planTypes.ZOOM_PHONE)
      const hasBundleZpPlan = this.getSelectedPlans.some(item => BUNDLE_WITH_ZP_BASE_PLAN_TYPES.includes(item.type));
      if (!hasBundleZpPlan) {
        const zoomPhonePlans = computedPbxPlans(this.getSelectedPlans);
        this.batchRemovePlan(zoomPhonePlans);
      }
      if(this.$context.is.fromPersistentCart) {
        this.groomDraftPlanFilterKeys();
      }
    },
    showCallingPlanToolTip () {
      this.$tracking.buyInteraction('zoom phone options', 'What calling plans do your users need? - tooltip');
    },
    showAdditionalNumbersToolTip () {
      this.$tracking.buyInteraction('zoom phone options', 'Add additional numbers - tooltip');
    },
    showCallingfeesToolTip () {
      this.$tracking.buyInteraction('zoom phone options', 'Choose how you’d like to pay - tooltip');
    }
  },
  watch: {
    selectedTFPlan(value) {
      if(!value) {
        this.form.tollFreeNumber = 0;
      }
    },
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `zoom phone - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-pbx,.opc-pbx-regional {

  .zbo-input-number{
    @include media(mobile,tablet){
      margin: 10px 0;
    }
  }
  &-head{
    display: flex;
    font-weight: 600;
    font-size: 18px;
    align-items: center;

    @include media(mobile,tablet){
      align-items: flex-start;
    }

    &__logo{
      min-height: 32px;
    }
  }
  &__logo {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
    margin-right: 12px;
    &-icon {
      display: block;
      width: auto;
      height: 32px;
      margin-right: 12px;
    }
  }
  .opc-tag-radio{
    margin-bottom: 20px;
  }
  .label-title-1 {
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      margin-bottom: 20px;
      @include media(mobile,tablet){
        margin-bottom: 10px;
      }
  }
  .opc-card-cascade{
    margin-top: 20px;
  }
  &__main {
    margin-top: 24px;
    border-top: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
    padding-top: 24px;
    @include media(mobile,tablet){
      margin-top: 10px;
      padding-top: 10px;
    }
  }

  .zbo-banner {
    margin-bottom: 8px;
    &__content {
      font-size: 14px;
    }
  }
}
.opc-config {
  &__collapse {
    color: var(--zoom-color-text-neutral, #686f79);
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
  }
}
</style>
